import type {
  MiceOrderView,
  MiceFlightLeg,
  MiceLegDirection,
  MiceOutbound,
  MiceTraveler,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, type DataTableSubRow } from '@web/ui/core';
import { formatMoney, formatShortDay } from './need_format';
import { OrderStatusChip } from './order_status_chip';

const NODE_KEY = {
  OUTWARD: 'mice.tree.outbound',
  RETURN: 'mice.tree.inbound',
  BOTH: 'mice.tree.both',
} as const satisfies Record<MiceLegDirection, string>;

/** O voo de volta do pedido: o próprio voo numa Volta, o `inbound` num pedido de ida e volta. */
function backLeg(
  order: MiceOrderView,
  direction: MiceLegDirection,
): MiceFlightLeg | MiceOutbound | null {
  if (direction === 'RETURN') return order.outbound;
  return direction === 'BOTH' ? order.inbound : null;
}

/** "AD 4570 · 26/11"; `fallback` mostra "—" quando a coluna é do sentido do pedido mas falta o voo. */
function FlightText({
  leg,
  fallback = false,
}: Readonly<{ leg: MiceFlightLeg | MiceOutbound | null; fallback?: boolean }>) {
  if (!leg) return fallback ? <span className="text-foreground">—</span> : null;
  return (
    <div className="whitespace-nowrap tabular-nums text-foreground">
      {`${leg.carrier} ${leg.flightNumber} · ${formatShortDay(leg.date)}`}
    </div>
  );
}

/** O pedido numa sub-linha: cada célula cai na coluna da grade do viajante. */
function orderSubRow(
  { order, direction }: MiceTraveler['orders'][number],
  onOpenOrder: (order: MiceOrderView) => void,
): DataTableSubRow {
  const leg = order.outbound;
  const codes = order.vouchers.flatMap((v) => (v.voucherCode ? [v.voucherCode] : []));
  const locators = [...new Set(order.vouchers.flatMap((v) => (v.locator ? [v.locator] : [])))];
  return {
    id: order.id,
    cells: {
      traveler: (
        <div className="flex min-w-0 items-baseline gap-1 pl-6">
          <span className="font-medium text-foreground">{t(NODE_KEY[direction])}</span>
          {leg ? (
            <span className="font-mono text-micro text-muted-foreground">{`${leg.origin} → ${leg.destination}`}</span>
          ) : null}
        </div>
      ),
      journey: (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <OrderStatusChip status={order.status} />
          {order.error ? (
            <span className="text-micro text-destructive">{order.error.message}</span>
          ) : null}
          {order.error?.code === 'price_drift' && order.lastQuote ? (
            <span className="text-micro text-destructive">
              {t('mice.tree.priceDrift', { price: formatMoney(order.lastQuote.price) })}
            </span>
          ) : null}
        </div>
      ),
      outbound: (
        <FlightText leg={direction === 'RETURN' ? null : leg} fallback={direction !== 'RETURN'} />
      ),
      inbound: <FlightText leg={backLeg(order, direction)} />,
      value: (
        <div className="text-right tabular-nums text-foreground">
          {order.pricePaid ? formatMoney(order.pricePaid) : '—'}
        </div>
      ),
      next: (
        <div className="flex items-center gap-2">
          <div className="min-w-0">
            <div className="font-mono text-meta text-foreground">{codes.join(' / ') || '—'}</div>
            {locators.length > 0 ? (
              <div className="text-micro text-muted-foreground">
                {t('mice.tree.locator', { code: locators.join(' / ') })}
              </div>
            ) : null}
          </div>
          {order.vouchers.length > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onOpenOrder(order);
              }}
            >
              {t('mice.tree.voucher')}
            </Button>
          ) : null}
        </div>
      ),
    },
  };
}

/** Sub-linhas da grade, uma por pedido; `null` sem pedido (a linha não expande). */
export function orderSubRows(
  traveler: MiceTraveler,
  onOpenOrder: (order: MiceOrderView) => void,
): DataTableSubRow[] | null {
  if (traveler.orders.length === 0) return null;
  return traveler.orders.map((entry) => orderSubRow(entry, onOpenOrder));
}
